/**
 * buildQuoteComparison — per-BoM-line multi-supplier quote matrix resolver.
 */
import type { BomItem, Quote, Supplier, SupplyChainPlan } from '../types/supplyChain';
import { supplierRiskAssessment } from './supplyChainRisks';

const MS_PER_MONTH = (365.25 / 12) * 24 * 60 * 60 * 1000;

export interface QuoteComparisonRow {
  quote: Quote;
  supplier: Supplier;
  risk: ReturnType<typeof supplierRiskAssessment>;
  totalCostUsd: number;
  expectedOnSiteDate: string | null;
  meetsCompliance: boolean;
  codSlipMonths: number | null;
}

export interface QuoteComparison {
  bomItem: BomItem;
  rows: QuoteComparisonRow[];
  codTarget: string | null;
  summary: {
    lowestCostQuoteId?: string;
    fastestQuoteId?: string;
    lowestRiskQuoteId?: string;
  };
}

function supplierHasAllCerts(supplier: Supplier, needed: readonly string[]): boolean {
  if (needed.length === 0) return true;
  const have = new Set((supplier.certifications ?? []).map(c =>
    typeof c === 'string' ? c : c.id,
  ));
  return needed.every(n => have.has(n));
}

export function buildQuoteComparison(
  plan: SupplyChainPlan,
  bomItemId: string,
  cod: string | null,
  now: Date = new Date(),
): QuoteComparison | null {
  const bomItem = plan.bom.find(b => b.id === bomItemId);
  if (!bomItem) return null;

  const openQuotes = plan.quotes.filter(q => q.bomItemId === bomItemId && q.status === 'open');
  if (openQuotes.length === 0) return null;

  const supplierById = new Map(plan.suppliers.map(s => [s.id, s]));
  const reqs = bomItem.complianceRequirements ?? [];
  const codDate = cod ? new Date(cod) : null;
  const codValid = codDate !== null && !Number.isNaN(codDate.getTime());

  const rows: QuoteComparisonRow[] = openQuotes.map((quote) => {
    const supplier = supplierById.get(quote.supplierId) ?? {
      id: quote.supplierId,
      name: '(unknown supplier)',
      categories: [bomItem.category],
      hq: { country: '—' },
    };
    const risk = supplierRiskAssessment(supplier, now);
    const totalCostUsd = quote.extendedPriceUsd ?? (quote.unitCostUsd * bomItem.quantity);
    let expectedOnSiteDate: string | null = null;
    let codSlipMonths: number | null = null;
    if (codValid) {
      const onSite = new Date(codDate!.getTime() - quote.leadTimeMonths * MS_PER_MONTH);
      expectedOnSiteDate = onSite.toISOString();
      const slipMs = onSite.getTime() - codDate!.getTime();
      codSlipMonths = slipMs / MS_PER_MONTH;
    }
    return {
      quote,
      supplier,
      risk,
      totalCostUsd,
      expectedOnSiteDate,
      meetsCompliance: supplierHasAllCerts(supplier, reqs) && quote.certificationComplianceConfirmed !== false,
      codSlipMonths,
    };
  });

  rows.sort((a, b) => a.totalCostUsd - b.totalCostUsd);

  const riskOrder = { low: 0, medium: 1, high: 2, critical: 3 } as const;
  const lowestCost = rows[0];
  const fastest = rows.slice().sort((a, b) => a.quote.leadTimeMonths - b.quote.leadTimeMonths)[0];
  const lowestRisk = rows.slice().sort(
    (a, b) => riskOrder[a.risk.overallTier] - riskOrder[b.risk.overallTier],
  )[0];

  return {
    bomItem,
    rows,
    codTarget: cod,
    summary: {
      lowestCostQuoteId: lowestCost?.quote.id,
      fastestQuoteId: fastest?.quote.id,
      lowestRiskQuoteId: lowestRisk?.quote.id,
    },
  };
}

/** BoM lines with 2+ open quotes — candidates for compare UI. */
export function bomIdsWithComparableQuotes(plan: SupplyChainPlan | undefined): string[] {
  if (!plan) return [];
  const counts = new Map<string, number>();
  for (const q of plan.quotes) {
    if (q.status !== 'open') continue;
    counts.set(q.bomItemId, (counts.get(q.bomItemId) ?? 0) + 1);
  }
  return Array.from(counts.entries()).filter(([, n]) => n >= 2).map(([id]) => id);
}
